/* eslint-disable sonarjs/cognitive-complexity */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames/bind';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { CREATIVE_STATUS_BADGE, REJECTION_FILTER_OPTIONS, REJECTED_TYPE } from 'consts/creative';
import { usePageFilters } from 'customHooks/usePageFilters';
import { CreativeStatus, CreativesFilters } from 'components/common/types/Creative.types';
import { getLookupData } from 'modules/api/lookups';
import MultipleSwitchBox from 'lib/MultipleSwitchBox';
import { CodeNameModel, CreativeType } from 'components/common/types';
import { useSelector } from 'react-redux';
import { getIsReseller } from 'store/publisher/selectors';
import { FilterCreativeId, FilterCreativeType, FilterDsp } from 'components/common/FilterArea/Filters';
import { cancelFunctions } from 'utils/cancelFunctions';
import FilterArea from 'components/common/FilterArea';
import { CardTheme } from 'lib/Card/Card.types';
import FilterMultiStatus from 'components/common/FilterArea/Filters/FilterMultiStatus';
import { parseCreativesPreFilters } from '../utils/parseCreativesPreFilters';
import AdvancedFilters from './AdvancedFilters';
import { FilterAreaType } from './Filters.types';

const creativeTypeData = [
  {
    code: '',
    name: 'Select creative type',
  },
  {
    code: CreativeType.VIDEO,
    name: 'Video',
  },
  {
    code: CreativeType.BANNER,
    name: 'Banner',
  },
  {
    code: CreativeType.DYNAMIC,
    name: 'Dynamic',
  },
];

const cx = classNames.bind(null);

const Filters = (): React.ReactElement | null => {
  const isReseller = useSelector(getIsReseller);
  const [searchParams] = useSearchParams();
  const hadDeletedPreFilter = useRef(searchParams.has('deleted')).current;
  const {
    filters: filterData,
    filters: {
      startDate,
      endDate,
      status,
      automatic,
      dsp,
      deleted,
      creativeType,
      externalId,
      advertiser,
      brand,
      productCategory,
      externalAdvertiserName,
      clickThroughUrl,
      externalAgency,
      productFormat,
      dealIds,
      width,
      height,
    },
    changeFilters: setCreativeFilters,
    resetFilters: resetCreativeFilters,
  } = usePageFilters<CreativesFilters>('content-management/creatives', parseCreativesPreFilters);

  const hasAdvancedFilters = useMemo(
    () =>
      !!(
        advertiser?.code ||
        brand?.code ||
        productCategory?.code ||
        externalAdvertiserName ||
        clickThroughUrl ||
        externalAgency ||
        productFormat?.code ||
        (dealIds && dealIds.length > 0) ||
        width ||
        height ||
        startDate ||
        endDate
      ),
    [
      advertiser,
      brand,
      productCategory,
      externalAdvertiserName,
      clickThroughUrl,
      externalAgency,
      productFormat,
      dealIds,
      width,
      height,
      startDate,
      endDate,
    ],
  );

  const [productFormatOptions, setProductFormatOptions] = useState<CodeNameModel[]>([]);

  const moderationFilter = CREATIVE_STATUS_BADGE.filter(
    (item) =>
      item.code !== CreativeStatus.UPLOADED &&
      item.code !== CreativeStatus.DELETED &&
      item.code !== CreativeStatus.RESTORED,
  ).flatMap((item) => (!isReseller && item.code === CreativeStatus.REJECTED ? REJECTION_FILTER_OPTIONS : [item]));

  const selectedStatusOptions = moderationFilter.filter(({ code }) => {
    if (code === REJECTED_TYPE.AUTO) return status?.includes(CreativeStatus.REJECTED) && automatic !== 'false';
    if (code === REJECTED_TYPE.MANUAL) return status?.includes(CreativeStatus.REJECTED) && automatic !== 'true';
    return status?.includes(code);
  });

  const onLoadLookupData = async (dropdownName: string, query: CodeNameModel | string): Promise<[]> => {
    try {
      return await getLookupData({ cancelFunctions, lookupURLName: dropdownName, query });
    } catch {
      return [];
    }
  };

  const getProductFormatOptions = async (): Promise<void> => {
    try {
      const response = await onLoadLookupData('productFormat', '');
      if (Array.isArray(response) && response.length > 0) {
        setProductFormatOptions(response);
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    !isReseller && getProductFormatOptions();

    if (!hadDeletedPreFilter) {
      setCreativeFilters({ deleted: false });
    }
  }, []);

  const onDropdownChange = (value: FilterAreaType, fieldName: string): void => {
    const getDateFilters = {
      ...filterData,
      startDate,
      endDate,
    };

    if (fieldName === 'startDate' && endDate && value > endDate) {
      getDateFilters.endDate = null;
    }

    setCreativeFilters({ ...getDateFilters, [fieldName]: value });
  };

  const onSelectedStatusesChange = (values: CodeNameModel[]): void => {
    const codes = values.map(({ code }) => code);
    const hasAuto = codes.includes(REJECTED_TYPE.AUTO);
    const hasManual = codes.includes(REJECTED_TYPE.MANUAL);

    setCreativeFilters({
      status: [
        ...new Set(
          codes.map((code) =>
            code === REJECTED_TYPE.AUTO || code === REJECTED_TYPE.MANUAL ? CreativeStatus.REJECTED : code,
          ),
        ),
      ],
      automatic: hasAuto === hasManual ? undefined : String(hasAuto),
    });
  };

  const onShowDeleted = (isDeleted: boolean): void =>
    setCreativeFilters({
      ...filterData,
      deleted: isDeleted,
      status: isDeleted ? [] : filterData.status,
      automatic: isDeleted ? undefined : filterData.automatic,
    });

  return Auth.hasPermission(PermissionsEnum.CONTENT_SEARCH) ? (
    <FilterArea
      customWrapperStyles="flex flex-wrap gap-4 w-full flex-1"
      defaultOpen={hasAdvancedFilters}
      advancedFilters={
        <AdvancedFilters
          onChange={onDropdownChange}
          advancedFilterData={filterData as CreativesFilters}
          productFormatOptions={productFormatOptions}
          onClearAll={() => {
            resetCreativeFilters('Creatives');
          }}
          changeFilters={setCreativeFilters}
        />
      }
      cardTheme={CardTheme.DEFAULT}
    >
      <div className="contents">
        <div className="flex-initial mr-4">
          <MultipleSwitchBox
            source={[
              { label: 'Library', value: false },
              { label: 'Deleted', value: true },
            ]}
            selectedValue={!!deleted}
            textKey="label"
            valueKey="value"
            onChange={onShowDeleted}
          />
        </div>
        {!deleted && (
          <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
            <FilterMultiStatus
              statusesList={moderationFilter}
              selectedValues={selectedStatusOptions}
              onSelectedStatusesChange={onSelectedStatusesChange}
            />
          </div>
        )}
        <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
          <FilterDsp onChange={(name, value) => setCreativeFilters({ [name]: value })} selectedValue={dsp} />
        </div>
        <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
          <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
            <FilterCreativeId
              changeFilters={(newFilter) => setCreativeFilters({ ...filterData, externalId: newFilter.creative })}
              creativeId={externalId}
            />
          </div>
        </div>
        <div
          className={cx('flex-grow flex-shrink-0 min-w-62.5', {
            'max-w-88 xl:max-w-100': deleted,
            'xl:max-w-100': !deleted,
          })}
        >
          <FilterCreativeType
            options={creativeTypeData}
            selectedValue={creativeTypeData.find((item) => item.code === creativeType)}
            onSelect={(_name, value: CodeNameModel) => {
              onDropdownChange(value.code, 'creativeType');
            }}
          />
        </div>
      </div>
    </FilterArea>
  ) : null;
};
export default Filters;

/* eslint-enable sonarjs/cognitive-complexity */
